// File: client/src/components/rules/expressionEditor/SampleDataContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSharedData } from '../../contexts/SharedDataContext';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const SampleDataContext = createContext();

export const useSampleData = () => useContext(SampleDataContext);

// Hard-coded default sample records (guaranteed to be available)
const DEFAULT_SAMPLE_RECORDS = [
    {
        id: 'sample-call-1',
        qualScore: 85,
        callType: 'inbound',
        agentName: 'John Doe',
        duration: 320,
        date: '2024-01-15T14:30:00Z',
        extraData: {
            processed: false,
            tags: ['sales', 'product-inquiry']
        },
        transcript: "Agent: Thank you for calling. How may I help you today?\nCustomer: Hi, I'm interested in your apartments.\nAgent: Great! I'd be happy to provide information about our available units.",
        isDefaultSample: true
    },
    {
        id: 'sample-call-2',
        qualScore: 65,
        callType: 'outbound',
        agentName: 'Jane Smith',
        duration: 180,
        date: '2024-01-16T10:15:00Z',
        extraData: {
            processed: true,
            tags: ['support', 'technical-issue']
        },
        transcript: "Agent: Hello, this is Jane from property management. I'm calling about your recent inquiry.\nCustomer: Yes, I submitted a maintenance request yesterday.\nAgent: I have that information here. When would be a good time for our team to visit?",
        isDefaultSample: true
    },
    {
        id: 'sample-call-3',
        qualScore: 92,
        callType: 'inbound',
        agentName: 'Robert Johnson',
        duration: 450,
        date: '2024-01-17T11:20:00Z',
        customerType: 'New Customer',
        extraData: {
            processed: false,
            priority: 'high',
            tags: ['sales', 'new-account']
        },
        transcript: "Agent: Thank you for calling our leasing office. My name is Robert. How can I help you?\nCustomer: Hi Robert, I saw your listing for the 2-bedroom apartment. Is it still available?\nAgent: Yes, we have several 2-bedroom units available. Are you interested in scheduling a tour?",
        isDefaultSample: true
    }
];

export const SampleDataProvider = ({ children }) => {
    const { selectedTenant } = useSharedData();
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    // Always initialize with guaranteed default samples
    const [sampleRecords, setSampleRecords] = useState(DEFAULT_SAMPLE_RECORDS);
    const [currentSampleIndex, setCurrentSampleIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetchTime, setLastFetchTime] = useState(null);

    // Get the current sample record (with fallback)
    const currentSample = useMemo(() => {
        const sample = sampleRecords[currentSampleIndex] || sampleRecords[0] || DEFAULT_SAMPLE_RECORDS[0];
        return sample;
    }, [sampleRecords, currentSampleIndex]);

    // Fetch sample records from API with caching
    const fetchSampleRecords = async (forceRefresh = false) => {
        // Skip if not authenticated or no tenant selected
        if (!isAuthenticated || !selectedTenant) return;

        // Check if we should use cached data (refresh every 30 minutes)
        const now = new Date();
        if (!forceRefresh && lastFetchTime && (now - lastFetchTime < 30 * 60 * 1000)) {
            return; // Use cached data
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get('/api/calls/samples', {
                params: { limit: 10 },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                // Combine fetched records with defaults to ensure we always have samples
                // Add tenant-specific data at the beginning of the array
                const tenantSamples = response.data.map(record => ({
                    ...record,
                    isTenantSample: true
                }));

                const combinedRecords = [...tenantSamples, ...DEFAULT_SAMPLE_RECORDS];

                // Remove duplicates by ID
                const uniqueRecords = [];
                const ids = new Set();

                for (const record of combinedRecords) {
                    if (record.id && !ids.has(record.id)) {
                        ids.add(record.id);
                        uniqueRecords.push(record);
                    }
                }

                setSampleRecords(uniqueRecords);
                // Reset to first sample from the tenant
                setCurrentSampleIndex(0);
                // Update last fetch time
                setLastFetchTime(now);
            }
        } catch (err) {
            console.error('Error fetching sample records:', err);
            setError('Failed to load sample records');
            // We already have defaults loaded, so no need for further fallback
        } finally {
            setLoading(false);
        }
    };

    // Change to next sample
    const nextSample = () => {
        setCurrentSampleIndex((prevIndex) =>
            prevIndex + 1 >= sampleRecords.length ? 0 : prevIndex + 1
        );
    };

    // Change to previous sample
    const prevSample = () => {
        setCurrentSampleIndex((prevIndex) =>
            prevIndex - 1 < 0 ? sampleRecords.length - 1 : prevIndex - 1
        );
    };

    // Set a specific index
    const selectSample = (index) => {
        if (index >= 0 && index < sampleRecords.length) {
            setCurrentSampleIndex(index);
        }
    };

    // Add a custom sample
    const addCustomSample = (sample) => {
        // Ensure the sample has an ID
        const sampleWithId = {
            ...sample,
            id: sample.id || `custom-sample-${Date.now()}`,
            isCustomSample: true
        };

        setSampleRecords((prev) => [...prev, sampleWithId]);
        setCurrentSampleIndex(sampleRecords.length); // Select the new sample
    };

    // Load samples when tenant changes or on mount
    useEffect(() => {
        if (selectedTenant && isAuthenticated) {
            fetchSampleRecords();
        }
    }, [selectedTenant, isAuthenticated]);

    return (
        <SampleDataContext.Provider
            value={{
                sampleRecords,
                currentSample,
                currentSampleIndex,
                loading,
                error,
                nextSample,
                prevSample,
                selectSample,
                refreshSamples: (forceRefresh = true) => fetchSampleRecords(forceRefresh),
                addCustomSample
            }}
        >
            {children}
        </SampleDataContext.Provider>
    );
};

// Helper function to flatten an object with depth control (for autocompletion)
export const flattenObject = (obj, prefix = '', result = {}, depth = 0, maxDepth = 3) => {
    if (depth > maxDepth || !obj || typeof obj !== 'object') return result;

    Object.entries(obj).forEach(([key, value]) => {
        const propPath = prefix ? `${prefix}.${key}` : key;

        // Add the current property
        result[propPath] = value;

        // Recurse into objects, but not arrays
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            flattenObject(value, propPath, result, depth + 1, maxDepth);
        }
    });

    return result;
};
